import { useEffect, useState } from 'react';
import { isSameDay, msUntilMidnight } from '../utils/date.js';

/**
 * Today's date, kept honest while the app stays open.
 *
 * Anchoring `new Date()` once at mount is wrong for a habit tracker: phones
 * keep tabs alive for days, and a stale anchor leaves yesterday highlighted as
 * "today" and new habits filed under the wrong date.
 *
 * Two triggers, because neither alone is enough:
 *  - a timer set for the next local midnight, for a tab left visible;
 *  - a visibility check, because background tabs get their timers throttled
 *    or suspended and the timer may fire late, or not at all.
 *
 * @returns {Date} local midnight of the current day
 */
export function useToday() {
  const [today, setToday] = useState(() => new Date());

  useEffect(() => {
    let timer;

    // Only swap the object when the calendar day really changed — a new Date
    // on every tick would re-render the whole calendar for nothing.
    const sync = () => {
      setToday((current) => {
        const now = new Date();
        return isSameDay(current, now) ? current : now;
      });
      schedule();
    };

    const schedule = () => {
      clearTimeout(timer);
      timer = setTimeout(sync, msUntilMidnight());
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') sync();
    };

    schedule();
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  return today;
}
